import { fetchPsap } from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import {
  fetchUser,
  fetchAppCIDRs,
  fetchAppUsers,
  fetchAppRoles,
  fetchAllPermissions,
  setTokens,
  postAuthTokenSuccess,
} from '@rsos/sinatra';
import { getUrlParameter } from '@rsos/utils';
import { configureUserScope } from '@rsos/utils/sentry';
import {
  APP_INIT_START,
  APP_INIT_SUCCESS,
  APP_INIT_FAILED,
} from './actionTypes';

export const appInitStart = () => ({
  type: APP_INIT_START,
});

export const appInitSuccess = () => ({
  type: APP_INIT_SUCCESS,
});

export const appInitFailed = error => ({
  type: APP_INIT_FAILED,
  error,
});

const SESSION_EXPIRED_QUERY_PARAM = '?session_expired=true';

const hardRedirect = to => {
  window.location = to;
};

const initialize = () => (dispatch, store) => {
  dispatch(appInitStart());
  const isLoggedIn = store().sinatra.user.isLoggedIn;

  const accessToken = getUrlParameter('access_token');
  if (accessToken) {
    const tokens = {
      refresh_token: null,
      token: accessToken,
    };
    setTokens(tokens);
    dispatch(postAuthTokenSuccess(tokens));
  }

  return dispatch(fetchUser())
    .then(user => {
      if (user && user.profile) {
        configureUserScope(user.profile);
      }
      const { sinatra } = store();
      const currentApp = sinatra.user.currentRole.application;
      const org = sinatra.user.currentOrg;
      const foundOrg = org.roles.find(role => role.application === currentApp);
      const securityPermFound =
        foundOrg.permissions.includes('MANAGE_SECURITY');
      const usersPermFound = foundOrg.permissions.includes('MANAGE_USERS');

      // Await these so the app's initialized gate only opens once the
      // management data exists. selectManagementByApp returns undefined until
      // these *Success actions populate sinatra.management, so opening the gate
      // earlier renders components (UsersManagement, RolesManagement,
      // IPSecurity*) against undefined management and crashes.
      const fetchIfUserPermissionFound = () =>
        Promise.all([
          dispatch(fetchAppUsers(org.id, currentApp)),
          dispatch(fetchAppRoles(org.id, currentApp)),
          dispatch(fetchAllPermissions(org.id, currentApp)),
          dispatch(fetchPsap(`/psaps/${org.name}`)),
        ]);
      //TODO: take care of blocks like this in Erquy phase2
      //NOTE: this determins what should be fetched based on permissions. to fetch anything user should have following combos:
      // MANAGE_USERS && MANAGE_SECURITY
      // MANAGE_USERS || MANAGE_SECURITY
      if (usersPermFound || securityPermFound) {
        if (!usersPermFound && securityPermFound) {
          // only MANAGE_SECURITY found
          return dispatch(fetchAppCIDRs(org.id, currentApp));
        } else if (usersPermFound && !securityPermFound) {
          // only MANAGE_USERS found
          return fetchIfUserPermissionFound();
        } else {
          // both found
          return Promise.all([
            dispatch(fetchAppCIDRs(org.id, currentApp)),
            fetchIfUserPermissionFound(),
          ]);
        }
      } else {
        throw new Error('User does not have permissions to access'); // redirect, no right permissions
      }
    })
    .then(() => dispatch(appInitSuccess()))
    .catch(action => {
      const isRefreshTokenExpired = Boolean(action?.isRefreshTokenExpired);

      // Only redirect with session-expired UX when refresh-token renewal failed.
      if (isLoggedIn && isRefreshTokenExpired) {
        return hardRedirect(`/${SESSION_EXPIRED_QUERY_PARAM}`);
      }

      if (isLoggedIn) {
        if (action.error) {
          return dispatch(appInitFailed(action.error));
        }
        return dispatch(appInitFailed(action));
      }

      return hardRedirect('/');
    });
};

export default initialize;
